import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { useExpenseCategories } from '@/composables/expense/useExpenseCategories';
import { expenseCategorySchema, type ExpenseCategoryFormData } from '@/lib/validators';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { EmptyState } from '@/components/EmptyState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormDialog } from '@/components/FormDialog';
import { FormField } from '@/components/forms/FormField';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { FormCurrencyInput } from '@/components/forms/FormCurrencyInput';
import { formatCurrency } from '@/lib/formatters';

export function ExpenseCategoriesPage() {
  const { categories, isLoading, createCategory, updateCategory, deleteCategory } = useExpenseCategories();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  const form = useForm<ExpenseCategoryFormData>({
    resolver: zodResolver(expenseCategorySchema),
    defaultValues: { name: '', description: '', color: '', icon: '', budgetLimit: 0, parentId: '' },
  });

  const handleCreate = (data: ExpenseCategoryFormData) => {
    createCategory.mutate(data, { onSuccess: () => { setShowCreate(false); form.reset(); } });
  };

  const handleEdit = (cat: typeof categories[0]) => {
    setEditId(cat.id);
    form.reset({ name: cat.name, description: cat.description || '', color: cat.color || '', icon: cat.icon || '', budgetLimit: cat.budgetLimit ? Number(cat.budgetLimit) : 0, parentId: cat.parentId || '' });
  };

  const handleUpdate = (data: ExpenseCategoryFormData) => {
    if (!editId) return;
    updateCategory.mutate({ id: editId, data }, { onSuccess: () => { setEditId(null); form.reset(); } });
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <PageContainer title="Expense Categories" description="Manage expense categories" actions={<Button onClick={() => setShowCreate(true)}><Plus className="mr-2 h-4 w-4" />Add Category</Button>}>
      {categories.length === 0 ? (
        <EmptyState title="No categories" description="Create your first expense category" actionLabel="Add Category" onAction={() => setShowCreate(true)} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {categories.map((cat) => (
            <Card key={cat.id}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base flex items-center gap-2">
                  {cat.color && <div className="h-3 w-3 rounded-full" style={{ backgroundColor: cat.color }} />}
                  {cat.name}
                </CardTitle>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(cat)}><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDeleteId(cat.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              </CardHeader>
              <CardContent>
                {cat.description && <p className="text-sm text-muted-foreground mb-2">{cat.description}</p>}
                {cat.budgetLimit && <p className="text-sm">Budget: {formatCurrency(cat.budgetLimit)}</p>}
                {cat.children && cat.children.length > 0 && <p className="text-xs text-muted-foreground mt-2">{cat.children.length} subcategories</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <FormDialog open={showCreate} onOpenChange={(open) => { setShowCreate(open); if (!open) form.reset(); }} title="Add Category" description="Create a new expense category" onSubmit={form.handleSubmit(handleCreate)} isSubmitting={createCategory.isPending}>
        <FormField form={form} name="name" label="Category Name" required />
        <FormTextarea form={form} name="description" label="Description" />
        <FormField form={form} name="color" label="Color" placeholder="#3b82f6" />
        <FormCurrencyInput form={form} name="budgetLimit" label="Budget Limit" />
      </FormDialog>
      <FormDialog open={!!editId} onOpenChange={(open) => { if (!open) { setEditId(null); form.reset(); } }} title="Edit Category" description="Update expense category" submitLabel="Save" onSubmit={form.handleSubmit(handleUpdate)} isSubmitting={updateCategory.isPending}>
        <FormField form={form} name="name" label="Category Name" required />
        <FormTextarea form={form} name="description" label="Description" />
        <FormField form={form} name="color" label="Color" placeholder="#3b82f6" />
        <FormCurrencyInput form={form} name="budgetLimit" label="Budget Limit" />
      </FormDialog>
      <ConfirmDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)} title="Delete Category" description="Are you sure?" variant="destructive" confirmLabel="Delete" onConfirm={() => { if (deleteId) { deleteCategory.mutate(deleteId); setDeleteId(null); } }} />
    </PageContainer>
  );
}
